/**
 * ErrorBoundary ABBUSINESS
 *
 * Capte les erreurs de rendu React et affiche une interface de secours
 * sans exposer les détails techniques à l'utilisateur.
 *
 * Fonctionnalités :
 *   - Classe React (obligatoire : les Error Boundaries ne peuvent pas être
 *     des fonctions — limitations de l'API React).
 *   - componentDidCatch : envoi vers Sentry + backend /v1/client-errors.
 *   - UI cohérente avec le design system ABBUSINESS (jetons CSS).
 *   - Bouton « Réessayer » : réinitialise le boundary (relance le rendu).
 *   - Bouton « Accueil » : navigation vers / sans rechargement complet.
 *   - Support dark mode via les jetons CSS existants.
 *
 * Usage :
 *   <ErrorBoundary>
 *     <Routes />
 *   </ErrorBoundary>
 *
 *   // Fallback personnalisé :
 *   <ErrorBoundary fallback={<MonFallback />}>
 *     ...
 *   </ErrorBoundary>
 */

import { Component } from 'react';
import { reportError } from '../services/telemetry';

// ── Log conditionnel ──────────────────────────────────────────────────────────

const isDev = import.meta.env.DEV === true;

// ── Composant ─────────────────────────────────────────────────────────────────

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError:     false,
      errorMessage: null,
      errorId:      null,
    };
    this.handleReset = this.handleReset.bind(this);
    this.handleHome  = this.handleHome.bind(this);
  }

  // ── Cycle de vie ────────────────────────────────────────────────────────

  static getDerivedStateFromError(error) {
    return {
      hasError:     true,
      errorMessage: error?.message ?? 'Une erreur inattendue est survenue.',
      errorId:      Date.now().toString(36),
    };
  }

  componentDidCatch(error, info) {
    if (isDev) {
      console.group('[ErrorBoundary] Erreur de rendu capturée');
      console.error(error);
      console.info('Arbre de composants :', info.componentStack);
      console.groupEnd();
    }

    // Envoi vers Sentry (si initialisé) ET vers le backend — via telemetry.js.
    reportError(error, {
      source:         'react-error-boundary',
      componentStack: info.componentStack,
    });
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  handleReset() {
    this.setState({ hasError: false, errorMessage: null, errorId: null });
  }

  handleHome() {
    // Réinitialisation du boundary puis navigation via history API
    this.setState({ hasError: false, errorMessage: null, errorId: null }, () => {
      window.history.pushState({}, '', '/');
      // Déclenche le routeur React Router sans rechargement complet
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
  }

  // ── Rendu ────────────────────────────────────────────────────────────────

  render() {
    if (! this.state.hasError) {
      return this.props.children;
    }

    // Fallback personnalisé (prop `fallback`)
    if (this.props.fallback) {
      return this.props.fallback;
    }

    const { errorMessage, errorId } = this.state;

    return (
      <>
        <style>{styles}</style>

        <div className="eb-root" role="alert" aria-live="assertive">
          <div className="eb-card">

            {/* Icône danger */}
            <div className="eb-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                width={36}
                height={36}
              >
                <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>

            {/* Titre */}
            <h1 className="eb-title">
              Une erreur est survenue
            </h1>

            {/* Message */}
            <p className="eb-message">
              {isDev
                ? errorMessage
                : 'Cette page a rencontré un problème inattendu. Nos équipes en sont informées.'}
            </p>

            {/* Code d'erreur (discret) */}
            {errorId && (
              <p className="eb-code" aria-label={`Code d'erreur : ${errorId}`}>
                Réf. {errorId}
              </p>
            )}

            {/* Actions */}
            <div className="eb-actions">
              <button
                type="button"
                className="eb-btn eb-btn--primary"
                onClick={this.handleReset}
              >
                Réessayer
              </button>

              <button
                type="button"
                className="eb-btn eb-btn--ghost"
                onClick={this.handleHome}
              >
                Accueil
              </button>
            </div>

          </div>
        </div>
      </>
    );
  }
}

// ── Styles inline (scoped) ────────────────────────────────────────────────────

/**
 * Les styles sont inline pour garantir l'affichage même si le CSS principal
 * ne se charge pas (la page en erreur peut avoir perdu ses imports CSS).
 *
 * Les jetons CSS du design system ABBUSINESS sont réutilisés directement :
 * --danger, --danger-wash, --surface-card, --ink, --ink-2, --ink-3,
 * --r-lg, --el-2, --sp-*.
 */
const styles = `
  .eb-root {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100dvh;
    padding: 24px;
    background: var(--surface-app, #F1F4F9);
    font-family: system-ui, -apple-system, sans-serif;
  }

  .eb-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    max-width: 440px;
    padding: 40px 32px;
    background: var(--surface-card, #FFFFFF);
    border-radius: var(--r-lg, 14px);
    box-shadow: var(--el-2, 0 2px 4px rgba(6,14,33,.05), 0 8px 24px rgba(6,14,33,.06));
    text-align: center;
  }

  .eb-icon {
    display: grid;
    place-items: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: var(--danger-wash, #FDECEC);
    color: var(--danger, #B91C1C);
    flex-shrink: 0;
  }

  .eb-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.25;
    color: var(--ink, #0F172A);
  }

  .eb-message {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: var(--ink-2, #475569);
    max-width: 360px;
  }

  .eb-code {
    margin: 0;
    font-size: 11px;
    color: var(--ink-3, #64748B);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.04em;
  }

  .eb-actions {
    display: flex;
    gap: 12px;
    margin-top: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .eb-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 40px;
    padding: 0 20px;
    border-radius: var(--r-md, 10px);
    font-size: 14px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: background 120ms ease, opacity 120ms ease;
  }

  .eb-btn:focus-visible {
    outline: 2px solid var(--accent, #003DA5);
    outline-offset: 2px;
  }

  .eb-btn--primary {
    background: var(--danger, #B91C1C);
    color: #fff;
  }

  .eb-btn--primary:hover {
    opacity: 0.88;
  }

  .eb-btn--ghost {
    background: var(--surface-sunken, #F7F9FC);
    color: var(--ink-2, #475569);
    border: 1px solid var(--line, #E2E8F2);
  }

  .eb-btn--ghost:hover {
    background: var(--surface-hover, #F4F7FC);
  }

  /* Thème sombre */
  @media (prefers-color-scheme: dark) {
    :root:not([data-theme="light"]) .eb-root {
      background: var(--surface-app, #070B14);
    }
    :root:not([data-theme="light"]) .eb-card {
      background: var(--surface-card, #0F1626);
    }
  }
  [data-theme="dark"] .eb-root {
    background: var(--surface-app, #070B14);
  }
  [data-theme="dark"] .eb-card {
    background: var(--surface-card, #0F1626);
  }
`;
